import { useEffect, useMemo, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowLeftRight, Loader2, MessageCircleWarning, Trash2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage, Badge, ScrollArea } from '@/components/ui';
import { useRequestGetConversationMessages, type IConversationMessage } from '@/hooks/httpRequest/reports/useRequestGetConversationMessages';
import { useRequestGetUserData } from '@/hooks/httpRequest/users/useRequestGetUserData';
import dayjs from '@/config/dayjs';
import routes from '@/lib/routes';
import { cn, getPhotoUrl } from '@/lib/utils';

// Helpers

function getInitial(username: string) {
	return username?.charAt(0)?.toUpperCase() || '?';
}

function groupByDate(messages: IConversationMessage[]) {
	const groups: { date: string; messages: IConversationMessage[] }[] = [];
	let currentDate = '';
	for (const msg of messages) {
		const label = dayjs(msg.createdAt).locale('fr').format('D MMMM YYYY');
		if (label !== currentDate) {
			currentDate = label;
			groups.push({ date: label, messages: [msg] });
		} else {
			groups[groups.length - 1].messages.push(msg);
		}
	}
	return groups;
}

function deriveParticipants(messages: IConversationMessage[], reporterUserId: string, reportedUserId: string) {
	const seen = new Map<string, string>();
	for (const msg of messages) {
		if (!seen.has(msg.from)) seen.set(msg.from, msg.username);
		if (seen.size === 2) break;
	}

	const entries = Array.from(seen.entries());
	if (entries.length === 0) return null;

	// anchor on reportedUserId first — reporterUserId can be the system user (no message sent)
	if (reportedUserId && seen.has(reportedUserId)) {
		const reportedUsername = seen.get(reportedUserId)!;
		const otherEntry = entries.find(([id]) => id !== reportedUserId);
		return {
			left: otherEntry ? { id: otherEntry[0], username: otherEntry[1] } : null,
			right: { id: reportedUserId, username: reportedUsername },
			leftIsReporter: !!reporterUserId && otherEntry?.[0] === reporterUserId,
		};
	}

	if (reporterUserId && seen.has(reporterUserId)) {
		const reporterUsername = seen.get(reporterUserId)!;
		const reportedEntry = entries.find(([id]) => id !== reporterUserId);
		return {
			left: { id: reporterUserId, username: reporterUsername },
			right: reportedEntry ? { id: reportedEntry[0], username: reportedEntry[1] } : null,
			leftIsReporter: true,
		};
	}

	return {
		left: entries[0] ? { id: entries[0][0], username: entries[0][1] } : null,
		right: entries[1] ? { id: entries[1][0], username: entries[1][1] } : null,
		leftIsReporter: false,
	};
}

// Sub-components

function ParticipantAvatar({
	username,
	side,
	className,
	photoUrl,
}: {
	username: string;
	side: 'left' | 'right';
	className?: string;
	photoUrl?: string;
}) {
	const isLeft = side === 'left';
	return (
		<Avatar className={cn('h-10 w-10', className)}>
			{photoUrl && <AvatarImage src={photoUrl} alt={username} className='object-cover' />}
			<AvatarFallback
				className={cn(
					'text-sm font-semibold',
					isLeft ? 'bg-secondary text-secondary-foreground' : 'bg-purple-200 text-purple-800',
				)}
			>
				{getInitial(username)}
			</AvatarFallback>
		</Avatar>
	);
}

function ParticipantCard({
	participant,
	side,
	role,
	photoUrl,
}: {
	participant: { id: string; username: string } | null;
	side: 'left' | 'right';
	role: 'Signaleur' | 'Signalé' | 'Participant';
	photoUrl?: string;
}) {
	const isLeft = side === 'left';

	if (!participant) {
		return <div className='flex-1 min-w-0' />;
	}

	return (
		<Link
			to={routes.nav.users.single.getHref(participant.id)}
			target='_blank'
			rel='noreferrer'
			className={cn(
				'group flex flex-1 min-w-0 items-center gap-3 rounded-xl p-2 -m-2 transition-colors hover:bg-muted/60',
				!isLeft && 'flex-row-reverse text-right',
			)}
		>
			<ParticipantAvatar username={participant.username} side={side} photoUrl={photoUrl} />
			<div className='min-w-0'>
				<p className='text-sm font-semibold truncate group-hover:underline'>{participant.username}</p>
				<Badge
					variant='outline'
					className={cn(
						'mt-1 font-medium',
						isLeft
							? 'bg-secondary/60 border-muted-foreground/20 text-secondary-foreground'
							: 'bg-purple-100 border-purple-200 text-purple-700',
					)}
				>
					{role}
				</Badge>
			</div>
		</Link>
	);
}

function DateSeparator({ label }: { label: string }) {
	return (
		<div className='sticky top-2 z-10 flex items-center gap-3 my-4 bg-card/95 backdrop-blur-xs py-1'>
			<div className='flex-1 h-px bg-border' />
			<span className='text-xs text-muted-foreground font-medium px-2.5 py-1 bg-background border border-border rounded-full whitespace-nowrap shadow-xs'>
				{label}
			</span>
			<div className='flex-1 h-px bg-border' />
		</div>
	);
}

function MessageBubble({
	msg,
	side,
	showAvatar,
	username,
	photoUrl,
}: {
	msg: IConversationMessage;
	side: 'left' | 'right';
	showAvatar: boolean;
	username: string;
	photoUrl?: string;
}) {
	const isLeft = side === 'left';
	const isDeleted = !!msg.deletedAt;
	const time = dayjs(msg.createdAt).format('HH:mm');

	const bubbleBg = isDeleted
		? 'bg-muted border border-dashed border-border'
		: isLeft
			? 'bg-secondary border border-border'
			: 'bg-purple-50 border border-purple-100';

	const textColor = isDeleted
		? 'text-muted-foreground italic'
		: isLeft
			? 'text-foreground'
			: 'text-purple-900';

	const timeColor = isLeft ? 'text-muted-foreground/60' : 'text-purple-400';

	return (
		<div className={`flex items-end gap-2 ${isLeft ? 'flex-row' : 'flex-row-reverse'}`}>
			<div className='w-8 shrink-0'>
				{showAvatar && (
					<Link to={routes.nav.users.single.getHref(msg.from)} target='_blank' rel='noreferrer'>
						<ParticipantAvatar username={username} side={side} className='h-8 w-8' photoUrl={photoUrl} />
					</Link>
				)}
			</div>
			<div className={`flex flex-col gap-0.5 min-w-0 max-w-[65%] sm:max-w-md ${isLeft ? 'items-start' : 'items-end'}`}>
				{showAvatar && (
					<Link
						to={routes.nav.users.single.getHref(msg.from)}
						target='_blank'
						rel='noreferrer'
						className='text-xs font-semibold text-muted-foreground px-1 hover:text-foreground hover:underline transition-colors w-fit'
					>
						{username}
					</Link>
				)}
				<div className={`rounded-2xl px-4 py-2.5 overflow-hidden ${bubbleBg} ${isLeft ? 'rounded-tl-sm' : 'rounded-tr-sm'}`}>
					{isDeleted ? (
						<div className='flex items-center gap-1.5'>
							<Trash2 size={12} className='text-muted-foreground' />
							<span className={`text-sm ${textColor}`}>Message supprimé</span>
						</div>
					) : (
						<p className={`text-sm leading-relaxed whitespace-pre-wrap break-all ${textColor}`}>
							{msg.text}
						</p>
					)}
					<div className='flex justify-end mt-1'>
						<span className={`text-xs ${timeColor}`}>{time}</span>
					</div>
				</div>
			</div>
		</div>
	);
}

// Page

const ReportConversationPage = () => {
	const { conversationId } = useParams<{ conversationId: string }>();
	const [searchParams] = useSearchParams();
	const reporterUserId = searchParams.get('reporterUserId') ?? '';
	const reportedUserId = searchParams.get('reportedUserId') ?? '';

	const { messages, isLoadingMessages, error } = useRequestGetConversationMessages(conversationId ?? '');

	const participants = useMemo(
		() => deriveParticipants(messages, reporterUserId, reportedUserId),
		[messages, reporterUserId, reportedUserId],
	);

	const { userData: leftUserData } = useRequestGetUserData(
		{ userId: participants?.left?.id ?? '' },
		!!participants?.left?.id,
	);
	const { userData: rightUserData } = useRequestGetUserData(
		{ userId: participants?.right?.id ?? '' },
		!!participants?.right?.id,
	);
	const leftPhotoUrl = leftUserData?.pictures?.[0]?.path ? getPhotoUrl(leftUserData.pictures[0].path) : undefined;
	const rightPhotoUrl = rightUserData?.pictures?.[0]?.path ? getPhotoUrl(rightUserData.pictures[0].path) : undefined;

	const grouped = useMemo(() => groupByDate(messages), [messages]);

	const cardRef = useRef<HTMLDivElement>(null);
	const viewportRef = useRef<HTMLDivElement>(null);

	// Fill viewport height from card top - works around main's broken h-full chain in App.tsx
	useEffect(() => {
		const card = cardRef.current;
		if (!card) return;
		const sync = () => {
			const { top } = card.getBoundingClientRect();
			card.style.height = `${window.innerHeight - top - 16}px`;
		};
		sync();
		window.addEventListener('resize', sync);
		return () => window.removeEventListener('resize', sync);
	}, []);

	useEffect(() => {
		if (viewportRef.current && !isLoadingMessages) {
			viewportRef.current.scrollTop = viewportRef.current.scrollHeight;
		}
	}, [isLoadingMessages, messages]);

	return (
		<div className='p-4 bg-background'>
			<div className='max-w-3xl mx-auto'>

				{/* Chat card */}
				<div ref={cardRef} className='bg-card rounded-2xl border border-border shadow-xs overflow-hidden flex flex-col'>

					{/* Top bar */}
					<div className='shrink-0 flex items-center justify-between px-4 py-2.5 border-b border-border'>
						<div className='flex items-center gap-2 text-xs text-muted-foreground'>
							<MessageCircleWarning size={13} />
							<span>Conversation signalée</span>
							{messages.length > 0 && (
								<span>· {messages.length} message{messages.length !== 1 ? 's' : ''}</span>
							)}
						</div>
						<button
							type='button'
							onClick={() => window.close()}
							className='flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors'
						>
							<ArrowLeft size={13} />
							Retour
						</button>
					</div>

					{/* Participants */}
					{participants && (participants.left || participants.right) && (
						<div className='shrink-0 flex items-center gap-3 px-4 py-3 border-b border-border bg-muted/20'>
							<ParticipantCard
								participant={participants.left}
								side='left'
								role={participants.leftIsReporter ? 'Signaleur' : 'Participant'}
								photoUrl={leftPhotoUrl}
							/>
							<ArrowLeftRight size={16} className='shrink-0 text-muted-foreground/50' />
							<ParticipantCard participant={participants.right} side='right' role='Signalé' photoUrl={rightPhotoUrl} />
						</div>
					)}

					{/* Messages */}
					{isLoadingMessages ? (
						<div className='flex-1 flex items-center justify-center'>
							<Loader2 className='animate-spin text-muted-foreground' size={28} />
						</div>
					) : error ? (
						<div className='flex-1 flex flex-col items-center justify-center gap-3'>
							<MessageCircleWarning size={36} className='text-destructive/40' />
							<p className='text-sm text-muted-foreground'>
								{error instanceof Error ? error.message : 'Impossible de charger cette conversation.'}
							</p>
						</div>
					) : messages.length === 0 ? (
						<div className='flex-1 flex flex-col items-center justify-center gap-3'>
							<MessageCircleWarning size={36} className='text-muted-foreground/30' />
							<p className='text-sm text-muted-foreground'>Aucun message trouvé pour cette conversation.</p>
						</div>
					) : (
						<ScrollArea className='flex-1' viewportRef={viewportRef}>
							<div className='flex flex-col gap-1.5 px-4 py-6'>
								{grouped.map(group => {
									let prevFrom: string | null = null;
									return (
										<div key={group.date}>
											<DateSeparator label={group.date} />
											<div className='flex flex-col gap-2'>
												{group.messages.map(msg => {
													const side: 'left' | 'right' = msg.from === participants?.left?.id ? 'left' : 'right';
													const username =
														side === 'left'
															? (participants?.left?.username ?? '')
															: (participants?.right?.username ?? '');
													const showAvatar = msg.from !== prevFrom;
													prevFrom = msg.from;
													const photoUrl = side === 'left' ? leftPhotoUrl : rightPhotoUrl;
													return (
														<MessageBubble
															key={msg._id}
															msg={msg}
															side={side}
															showAvatar={showAvatar}
															username={username}
															photoUrl={photoUrl}
														/>
													);
												})}
											</div>
										</div>
									);
								})}
							</div>
						</ScrollArea>
					)}
				</div>

			</div>
		</div>
	);
};

export default ReportConversationPage;
